import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { replace } from 'connected-react-router';
import moment from 'moment-timezone';
import { debounce } from 'lodash';

import Strings from '../../Strings';
import { GET_SHIPMENTS_RESULT, shippingActions } from './actions';
import './Shipping.scss';
import ShipmentList from './ShipmentList';
import { shipmentStatusEnum } from '../../modals/_ModalsMetadata/Fields/shipmentFields';
import { Tabs, Tab } from '../../components/Tabs';
import AdvancedFilters, { Filter } from '../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition, OrgFilter } from '../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../components/AdvancedFilters/AdvancedFiltersBar';
import { TextInput } from '../../components/PageHeader/TextInput';
import { getFiltersForRequest, getRelevantFilters } from '../../components/AdvancedFilters/helpers';
import Select from '../../components/Select';
import { CUSTOM, DateFilters } from '../../utils/DateFilters';
import { openModalAction } from '../../actions/modal';
import { isShipper, isSuperUser } from '../../utils/userRoles';
import { useGlobalPageSize } from '../../utils/tableUtils';

export const ShipmentsTabs = {
  IN_PROGRESS: {
    name: Strings.inProgress,
    key: 'shipping-in_progress',
    url: '/shipping/in-progress',
    statuses: [shipmentStatusEnum.new, shipmentStatusEnum.created],
  },
  IN_TRANSIT: {
    name: Strings.shipping.shipmentStatuses.in_transit,
    key: 'shipping-in_transit',
    url: '/shipping/in-transit',
    statuses: [shipmentStatusEnum.in_transit],
  },
  DELIVERED: {
    name: Strings.shipping.shipmentStatuses.delivered,
    key: 'shipping-delivered',
    url: '/shipping/delivered',
    statuses: [shipmentStatusEnum.delivered],
  },
  CANCELLED: {
    name: Strings.shipping.shipmentStatuses.cancelled,
    key: 'shipping-cancelled',
    url: '/shipping/cancelled',
    statuses: [shipmentStatusEnum.cancelled],
  }
}
export const getShippedDate = shipment => {
  return shipment?.status_history?.find(s => s.status === shipmentStatusEnum.in_transit)?.created_at;
};
  
export const getDeliveredDate = shipment => {
  return shipment.status_history?.find(s => s.status === shipmentStatusEnum.delivered)?.created_at;
};


function Shipping(props) {
  const { orgs, refreshData, refreshTimestamp, replacePath, openDateRangeSelectionModal, filters, getShipments } = props;
  const globalPageSize = useGlobalPageSize();
  const urlParams = new URLSearchParams(window.location.search);
  const statusFromUrl = urlParams.get('status');

  const [status, setStatus] = React.useState(statusFromUrl || Object.values(ShipmentsTabs)[0].key);
  const [shipments, setShipments] = React.useState([]);
  const [pagination, setPagination] = React.useState({ offset: 0, limit: globalPageSize, totalRecords: 0 });
  const [statusCounts, setStatusCounts] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [dateRange, setDateRange] = React.useState(DateFilters.AllTime.value);
  const pageRequest = React.useRef({ offset: 0, limit: globalPageSize });
  const requestSequence = React.useRef(0);
  const canIncludeInactivePatients = isShipper() || isSuperUser();

  const includeInactivePatients = React.useMemo(() => {
    if (!canIncludeInactivePatients) {
      return false;
    }

    const inactivePatientsFilter = getRelevantFilters(filters).find(filter => filter.group === 'includeInactivePatients');
    return inactivePatientsFilter?.value === 'true';
  }, [canIncludeInactivePatients, filters]);

  const loadData = React.useCallback(async () => {
    requestSequence.current += 1;
    const sequence = requestSequence.current;
    setLoading(true);
    try {
      const resp = await getShipments(pageRequest.current);
      if (sequence !== requestSequence.current) return;
      if (resp?.type === GET_SHIPMENTS_RESULT) {
        setShipments(resp.response?.data || []);
        setPagination(resp.response?.pagination || { ...pageRequest.current, totalRecords: 0 });
        setStatusCounts(resp.response?.status_counts || {});
      }
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [getShipments]);

  React.useEffect(() => {
    if (status) {
      const newUrlParams = new URLSearchParams(window.location.search);
      newUrlParams.set('status', status);
      replacePath(`${window.location.pathname}?${newUrlParams}`);
    }
  }, [status]);

  const onSearchQueryChange = React.useMemo(() => debounce(search => {
    pageRequest.current = { ...pageRequest.current, search, offset: 0 };
    loadData();
  }, 300), [loadData]);

  React.useEffect(() => () => onSearchQueryChange.cancel(), [onSearchQueryChange]);

  const onDateRangeChanged = newRange => {
    if (newRange?.value === CUSTOM) {
      const onDateSubmited = dates => {
        const startDate = dates.startDate ? moment(dates.startDate * 1000) : undefined;
        const endDate = dates.endDate ? moment(dates.endDate * 1000) : undefined;
        setDateRange({ startDate, endDate });
      };
      openDateRangeSelectionModal({ actionOnDone: onDateSubmited });
    } else if (newRange && newRange?.value !== DateFilters.AllTime.value) {
      setDateRange({ startDate: moment(newRange.dates.startDate()), endDate: moment(newRange.dates.endDate()) });
    } else {
      setDateRange({ startDate: undefined, endDate: undefined });
    }
  };

  React.useEffect(() => {
    const selectedTab = Object.values(ShipmentsTabs).find(tab => tab.key === status) || ShipmentsTabs.IN_PROGRESS;
    const relevantGroups = ['flow', 'order_category', 'fulfillment_provider'];
    const filterBy = getFiltersForRequest(filters.filter(filter => relevantGroups.includes(filter.group)));
    filterBy.push(`status:${selectedTab.statuses.join(',')}`);
    const orgId = getRelevantFilters(filters).filter(filter => filter.group === 'orgId').map(filter => filter.value);
    pageRequest.current = {
      ...pageRequest.current,
      offset: 0,
      filterBy,
      orgId: orgId.length ? orgId : undefined,
      startTimestamp: dateRange?.startDate?.toISOString(),
      endTimestamp: dateRange?.endDate?.toISOString(),
      includeInactivePatients: canIncludeInactivePatients ? includeInactivePatients : undefined,
    };
    loadData();
  }, [canIncludeInactivePatients, dateRange, filters, includeInactivePatients, loadData, refreshTimestamp, status]);

  const displayedShipments = React.useMemo(() => shipments.map(shipment => ({
    ...shipment,
    orgName: orgs.find(org => org.masked_id === shipment.org_uuid)?.name,
  })), [orgs, shipments]);

  const onOffsetChange = offset => {
    pageRequest.current = { ...pageRequest.current, offset };
    loadData();
  };

  const onPageSizeChange = limit => {
    pageRequest.current = { ...pageRequest.current, offset: 0, limit };
    loadData();
  };

  const onSortingChange = sorting => {
    const next = sorting?.[0];
    const sortColumn = next?.id;
    const sortType = next ? (next.desc ? 'descending' : 'ascending') : undefined;
    if (pageRequest.current.sortColumn === sortColumn && pageRequest.current.sortType === sortType) return;
    pageRequest.current = { ...pageRequest.current, offset: 0, sortColumn, sortType };
    loadData();
  };

  const shipmentToolbar = (
    <>
      <TextInput placeholder={Strings.search} onChange={e => onSearchQueryChange(e.target.value)} />
      <Select
        data={[
          DateFilters.Custom,
          DateFilters.Last48Hours,
          DateFilters.ThisMonth,
          DateFilters.Last30Days,
          DateFilters.LastMonth,
          DateFilters.Last6Months,
          DateFilters.AllTime,
        ]}
        onChange={onDateRangeChanged}
        defaultValue={DateFilters.AllTime.value}
      />
      <AdvancedFilters>
        <Filter definition={getFiltersDefinition().flow} />
        <Filter definition={getFiltersDefinition().order_category} />
        {orgs?.length > 0 && <Filter definition={OrgFilter(orgs).orgId} />}
        <Filter definition={getFiltersDefinition().fulfillment_provider} />
        {canIncludeInactivePatients && <Filter definition={getFiltersDefinition().includeInactivePatients} />}
      </AdvancedFilters>
    </>
  );

  const tabDefinitions = [
    {
      ...ShipmentsTabs.IN_PROGRESS,
      count: (statusCounts.new || 0) + (statusCounts.created || 0),
      status: shipmentStatusEnum.new,
    },
    {
      ...ShipmentsTabs.IN_TRANSIT,
      count: statusCounts.in_transit || 0,
      status: shipmentStatusEnum.in_transit,
    },
    {
      ...ShipmentsTabs.DELIVERED,
      count: statusCounts.delivered || 0,
      status: shipmentStatusEnum.delivered,
    },
    {
      ...ShipmentsTabs.CANCELLED,
      count: statusCounts.cancelled || 0,
      status: shipmentStatusEnum.cancelled,
    },
  ];

  const tabArray = tabDefinitions.map(tab => (
    <Tab label={tab.name} key={tab.key} count={tab.count}>
      <ShipmentList
        shipments={displayedShipments}
        status={tab.status}
        loading={loading}
        orgs={orgs}
        refreshData={refreshData}
        pagination={pagination}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
      />
    </Tab>
  ));

  return (
    <Tabs
      containerClassName="shipping-list-tabs"
      defaultIndex={tabArray.findIndex(t => t.key === status)}
      onChange={i => setStatus(tabArray[i].key)}
      rightContent={shipmentToolbar}
      contentHeader={<AdvancedFiltersBar customFilters={OrgFilter(orgs)} />}
    >
      {tabArray}
    </Tabs>
  );
}

Shipping.propTypes = {
  orgs: PropTypes.array,
  getShipments: PropTypes.func.isRequired,
  refreshData: PropTypes.func.isRequired,
  replacePath: PropTypes.func.isRequired,
  openDateRangeSelectionModal: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
  refreshTimestamp: PropTypes.number,
};

const mapDispatchToProps = dispatch => ({
  getShipments: pageRequest => dispatch(shippingActions.getShipments(pageRequest)),
  openDateRangeSelectionModal: data => dispatch(openModalAction('data-range-selection-modalion', data)),
  replacePath: url => dispatch(replace(url)),
});

const mapStateToProps = state => {
  return {
    filters: state.entities.advancedFilters.filters.items,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Shipping);
